/**
 * Generic chat host. Two URL shapes:
 *   /chat/:id              → resume existing conversation
 *   /chat/new?mode=...     → fresh conversation in the given mode
 *                           (optional &kickoff=... and &addendum=...)
 */

import { useParams, useSearchParams } from "react-router-dom";

import { ChatView } from "@/features/chat/components/ChatView";
import {
  SESSION_MODES,
  type DrillDifficulty,
  type SessionMode,
} from "@/shared/models";

const VALID_DIFFICULTIES = new Set<DrillDifficulty>([
  "foundational",
  "default",
  "edge_case",
]);

function parseDifficulty(value: string | null): DrillDifficulty | undefined {
  if (!value) return undefined;
  return VALID_DIFFICULTIES.has(value as DrillDifficulty)
    ? (value as DrillDifficulty)
    : undefined;
}

export function Chat() {
  const params = useParams<{ id: string }>();
  const [search] = useSearchParams();

  if (params.id && params.id !== "new") {
    return <ChatView resumeConversationId={params.id} title="Chat" />;
  }

  const mode = (search.get("mode") ?? "just_ask") as SessionMode;
  const kickoff = search.get("kickoff") ?? undefined;
  const drillTarget = search.get("drill_target") ?? undefined;
  const drillDifficulty = parseDifficulty(search.get("drill_difficulty"));
  const dailyChallengeDate = search.get("daily_challenge_date") ?? undefined;
  const sessionId = search.get("session_id") ?? undefined;
  const sessionStake = search.get("session_stake") ?? undefined;
  const sessionLocation = search.get("session_location") ?? undefined;
  const handId = search.get("hand_id") ?? undefined;
  const title = dailyChallengeDate
    ? "Today's Drill"
    : SESSION_MODES[mode]?.title ?? "Chat";

  return (
    // Key on the search string so navigating between two /chat/new
    // launches (e.g. Today's Drill "Continue" → next rung, which only
    // changes query params on the same route) forces a fresh ChatView
    // mount. Without the key, React reuses the instance and the
    // mount-once kickoff effect never re-fires — the new drill never
    // starts.
    <ChatView
      key={search.toString()}
      initialMode={mode}
      kickoffMessage={kickoff}
      drillTarget={drillTarget}
      drillDifficulty={drillDifficulty}
      dailyChallengeDate={dailyChallengeDate}
      sessionId={sessionId}
      sessionStake={sessionStake}
      sessionLocation={sessionLocation}
      handId={handId}
      title={title}
    />
  );
}
